import { useQueryClient } from '@tanstack/react-query';
import { InputField } from 'fields/base/InputField/InputField';
import { useFormik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { fetchAllProjectsWithPairs } from '@/entities/projects-data/api/fetchAllProjectsWithPairs';
import { syncProjectsToRedux } from '@/entities/projects-data/lib/syncToRedux';
import { PROJECTS_DATA_QUERY_KEYS } from '@/entities/projects-data/model/projectsDataQueryKeys';

import { addSolanaPair, describeMeteoraDLMMPool } from 'entities/project/api/project/api';
import type {
  AddSolanaPairRequest,
  MeteoraDLMMPoolDescription,
} from 'entities/project/api/project/api.types';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch } from 'store';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { Modal } from 'ui/Modal/Modal';
import { solanaPoolDexLabels } from 'web3';

import {
  initialSolanaPairFormValues,
  type SolanaPairFormValues,
  toAddSolanaPairRequest,
  validateSolanaPairForm,
} from './model/form';

type AddSolanaPairModalProps = {
  dex: AddSolanaPairRequest['dex'];
  projectId: number;
  onClose: () => void;
};

const isVerifiedMeteoraDescription = (
  description: MeteoraDLMMPoolDescription | undefined,
  poolAddress: string,
): description is MeteoraDLMMPoolDescription => {
  if (!description || typeof description !== 'object') return false;

  return (
    description.dex === 'meteora_dlmm:solana' &&
    description.pool_address === poolAddress &&
    typeof description.base_mint === 'string' &&
    description.base_mint.length > 0 &&
    typeof description.quote_mint === 'string' &&
    description.quote_mint.length > 0 &&
    Number.isInteger(description.base_decimals) &&
    description.base_decimals >= 0 &&
    description.base_decimals <= 255 &&
    Number.isInteger(description.quote_decimals) &&
    description.quote_decimals >= 0 &&
    description.quote_decimals <= 255 &&
    Number.isInteger(description.snapshot_slot) &&
    description.snapshot_slot > 0
  );
};

export const AddSolanaPairModal = ({ dex, projectId, onClose }: AddSolanaPairModalProps) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dexLabel = solanaPoolDexLabels[dex];
  const isMeteora = dex === 'meteora_dlmm:solana';
  const [meteoraDescription, setMeteoraDescription] = useState<MeteoraDLMMPoolDescription | null>(
    null,
  );
  const [meteoraLookupState, setMeteoraLookupState] = useState<
    'idle' | 'invalid' | 'loading' | 'verified' | 'unverified'
  >('idle');
  const meteoraLookupSequence = useRef(0);

  const refreshProjects = useCallback(async () => {
    try {
      const projectsData = await fetchAllProjectsWithPairs();
      syncProjectsToRedux(dispatch, projectsData);
      queryClient.setQueryData(PROJECTS_DATA_QUERY_KEYS.list(), projectsData, {
        updatedAt: Date.now(),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECTS_DATA_QUERY_KEYS.all });
    } catch (error) {
      console.error('[AddSolanaPairModal] Failed to refresh projects', error);
    }
  }, [dispatch, queryClient]);

  const submit = useCallback(
    async (values: SolanaPairFormValues) => {
      if (
        isMeteora &&
        (meteoraLookupState !== 'verified' ||
          meteoraDescription?.pool_address !== values.poolAddress.trim())
      ) {
        toast.error('Verify the Meteora DLMM pool before creating the pair');
        return;
      }

      const response = await addSolanaPair(toAddSolanaPairRequest(projectId, dex, values));
      if (!response.isSuccess) {
        toast.error(response.errorMessage || `Failed to create ${dexLabel} pair`);
        return;
      }

      onClose();
      toast.success(`Created ${dexLabel} pair`);
      void refreshProjects();
      navigate(
        generatePath(ROUTE_PATHS.solPair, {
          projectId: projectId.toString(),
          pairId: response.data.id.toString(),
        }),
      );
    },
    [
      dex,
      dexLabel,
      isMeteora,
      meteoraDescription?.pool_address,
      meteoraLookupState,
      navigate,
      onClose,
      projectId,
      refreshProjects,
    ],
  );

  const { errors, handleBlur, handleSubmit, isSubmitting, setFieldValue, touched, values } =
    useFormik({
      initialValues: initialSolanaPairFormValues,
      onSubmit: submit,
      validate: validateSolanaPairForm,
    });

  const normalizedPoolAddress = values.poolAddress.trim();
  const hasValidPoolAddress = validateSolanaPairForm(values).poolAddress === undefined;

  useEffect(() => {
    const sequence = ++meteoraLookupSequence.current;

    if (!isMeteora) {
      setMeteoraDescription(null);
      setMeteoraLookupState('idle');
      return;
    }

    setMeteoraDescription(null);
    if (!hasValidPoolAddress) {
      setMeteoraLookupState('invalid');
      return;
    }

    const controller = new AbortController();
    let active = true;
    setMeteoraLookupState('loading');

    const lookupTimer = window.setTimeout(() => {
      void describeMeteoraDLMMPool(
        { projectId, poolAddress: normalizedPoolAddress },
        controller.signal,
      )
        .then(response => {
          if (!active || sequence !== meteoraLookupSequence.current) return;

          const description = response.isSuccess ? response.data : undefined;
          const isVerified = isVerifiedMeteoraDescription(description, normalizedPoolAddress);

          setMeteoraDescription(isVerified ? description : null);
          setMeteoraLookupState(isVerified ? 'verified' : 'unverified');
        })
        .catch(() => {
          if (!active || sequence !== meteoraLookupSequence.current) return;
          setMeteoraDescription(null);
          setMeteoraLookupState('unverified');
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(lookupTimer);
      controller.abort();
    };
  }, [hasValidPoolAddress, isMeteora, normalizedPoolAddress, projectId]);

  const setValue = useCallback(
    (field: keyof SolanaPairFormValues, value: string) => {
      void setFieldValue(field, value);
    },
    [setFieldValue],
  );

  const error = useCallback(
    (field: keyof SolanaPairFormValues) => (touched[field] ? errors[field] : undefined),
    [errors, touched],
  );

  return (
    <Modal
      title={`New ${dexLabel} pair`}
      onClose={onClose}
      className="mm-add-solana-pair-modal"
      customButtons={
        isSubmitting ? (
          <ButtonLoading />
        ) : (
          <Button
            type="button"
            disabled={
              isMeteora &&
              (meteoraLookupState !== 'verified' ||
                meteoraDescription?.pool_address !== normalizedPoolAddress)
            }
            onClick={() => handleSubmit()}
          >
            Create
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm text-secondary">
          Solana · {dexLabel}. The manager verifies the pool, vaults and mint details before saving.
          This action does not create wallets or start bots.
        </p>
        <InputField
          name="poolAddress"
          label={`${dexLabel} pool address`}
          type="text"
          value={values.poolAddress}
          setValue={value => setValue('poolAddress', value)}
          onBlur={handleBlur}
          errorMessage={error('poolAddress')}
        />
        {isMeteora && (
          <div className="min-w-0 text-sm" aria-live="polite" role="status">
            {meteoraLookupState === 'loading' && <p>Verifying Meteora pool...</p>}
            {meteoraLookupState === 'invalid' && (
              <p>Enter a canonical pool address to verify it.</p>
            )}
            {meteoraLookupState === 'unverified' && (
              <p role="alert">Meteora pool verification failed. The pair cannot be created.</p>
            )}
            {meteoraLookupState === 'verified' && meteoraDescription && (
              <dl className="min-w-0 space-y-1">
                <div>
                  <dt className="inline text-secondary">Base mint: </dt>
                  <dd className="inline break-all">{meteoraDescription.base_mint}</dd>
                  <span>
                    {' '}
                    ({meteoraDescription.base_symbol || 'unknown'},{' '}
                    {meteoraDescription.base_decimals} decimals)
                  </span>
                </div>
                <div>
                  <dt className="inline text-secondary">Quote mint: </dt>
                  <dd className="inline break-all">{meteoraDescription.quote_mint}</dd>
                  <span>
                    {' '}
                    ({meteoraDescription.quote_symbol || 'unknown'},{' '}
                    {meteoraDescription.quote_decimals} decimals)
                  </span>
                </div>
                <div>
                  <dt className="inline text-secondary">Verified at slot: </dt>
                  <dd className="inline">{meteoraDescription.snapshot_slot}</dd>
                </div>
              </dl>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
